import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import RouteIcon from '@mui/icons-material/Route';
import GridViewIcon from '@mui/icons-material/GridView';
import NavigationIcon from '@mui/icons-material/Navigation';
import { useLandingPageAppearance } from '../../context/LandingPageAppearanceContext';
import PortalTemplatePickerGrid from './PortalTemplatePickerGrid';
import SharedTemplateChooser from './SharedTemplateChooser';
import { LOGIN_PORTAL_THEMES } from '../../theme/loginPortalThemes';

function SelectorSection({ icon, title, subtitle, themeId, onSelect, defaultExpanded = false }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px !important',
        mb: 1.5,
        '&:before': { display: 'none' },
        overflow: 'hidden',
        bgcolor: 'background.paper'
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, minHeight: 52 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 0.5, minWidth: 0 }}>
          <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>{icon}</Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
              {subtitle}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={LOGIN_PORTAL_THEMES[themeId]?.label || themeId}
            sx={{ fontWeight: 600, height: 22, flexShrink: 0 }}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
        <PortalTemplatePickerGrid selectedId={themeId} onSelect={onSelect} showDetails columns="auto" />
      </AccordionDetails>
    </Accordion>
  );
}

export default function LandingTemplateSelector() {
  const {
    panelsTemplateId,
    setPanelsTemplateId,
    careerTemplateId,
    setCareerTemplateId,
    featuresTemplateId,
    setFeaturesTemplateId,
    navVariant,
    setNavVariant,
    syncToQueryPortal,
    resetAll
  } = useLandingPageAppearance();

  return (
    <SharedTemplateChooser
      title="Template chooser"
      subtitle="Page atmosphere is shared with Sign In, Register, and Browsing."
      topOffset={{ xs: 68, sm: 76 }}
    >
      <SelectorSection
        icon={<ViewQuiltIcon fontSize="small" />}
        title="Hero & entry panels"
        subtitle="Get-started card & hero accents"
        themeId={panelsTemplateId}
        onSelect={setPanelsTemplateId}
      />
      <SelectorSection
        icon={<RouteIcon fontSize="small" />}
        title="Career journey"
        subtitle="360° wheel & 100-step path"
        themeId={careerTemplateId}
        onSelect={setCareerTemplateId}
      />
      <SelectorSection
        icon={<GridViewIcon fontSize="small" />}
        title="Features & CTA"
        subtitle="Capability cards & footer"
        themeId={featuresTemplateId}
        onSelect={setFeaturesTemplateId}
      />
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px !important',
          bgcolor: 'background.paper',
          mb: 1.5,
          '&:before': { display: 'none' }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <NavigationIcon fontSize="small" color="primary" />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Top navigation
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Auto / Light / Dark bar
              </Typography>
            </Box>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1.5, pb: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={navVariant}
            onChange={(_, v) => v && setNavVariant(v)}
          >
            <ToggleButton value="auto" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
              Auto
            </ToggleButton>
            <ToggleButton value="light" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
              Light
            </ToggleButton>
            <ToggleButton value="dark" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
              Dark
            </ToggleButton>
          </ToggleButtonGroup>
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 1 }} />
      <Stack spacing={1} sx={{ pt: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={resetAll}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Reset landing extras
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
          Page atmosphere already syncs everywhere. Use Reset only for landing-only sections.
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={syncToQueryPortal}
          sx={{ textTransform: 'none', fontWeight: 600, display: 'none' }}
        >
          Sync
        </Button>
      </Stack>
    </SharedTemplateChooser>
  );
}
